import { Image } from 'expo-image';
import { Link, router, useLocalSearchParams } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { Button } from '@/src/components/Button';
import { ErrorView } from '@/src/components/ErrorView';
import { IdentityHeader } from '@/src/components/IdentityHeader';
import { PublicPageShell } from '@/src/components/PublicPageShell';
import { GarageSection } from '@/src/features/garage/GarageCard';
import { withLiveGarageVehicleOwnerProfile } from '@/src/features/garage/garage-owner-profile';
import type { GarageVehicle } from '@/src/features/garage/garage-service';
import { PostCard } from '@/src/features/posts/PostCard';
import { PostPhotoViewerModal } from '@/src/features/posts/PostPhotoViewerModal';
import { PostVideoViewerModal } from '@/src/features/posts/PostVideoViewerModal';
import {
  PhotoViewer,
  type PhotoContext,
} from '@/src/features/posts/PhotoAttachment';
import type { PublicPost } from '@/src/features/posts/post-service';
import {
  emptyProfileReactionCounts,
  getProfileAvatarOriginalURL,
  getProfileAvatarURL,
  getProfileCoverOriginalURL,
  getProfileCoverPhotoURL,
  totalProfileReactions,
  type PublicProfile,
  type PublicProfileStats,
} from '@/src/features/profile/profile-service';
import {
  ProfileContentTabs,
  type ProfileContentTab,
} from '@/src/features/profile/ProfileContentTabs';
import { ProfileReactionBreakdownModal } from '@/src/features/profile/ProfileStatsModals';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { fetchPublicJson } from '@/src/utils/public-api';

type PublicProfilePayload = {
  profile: PublicProfile | null;
  posts: PublicPost[];
  stats?: PublicProfileStats;
  vehicles: GarageVehicle[];
};

type ProfileViewerImage = {
  accessibilityLabel: string;
  analyticsContext: PhotoContext;
  originalURL: string;
  thumbnailURL: string | null;
  title: string;
};

async function fetchPublicProfile(username: string) {
  const { data, response } = await fetchPublicJson<PublicProfilePayload>(
    `/api/public/profiles/${encodeURIComponent(username)}`,
  );

  if (!response.ok && response.status !== 404) {
    throw new Error('Unable to load this profile.');
  }

  return data;
}

export default function PublicUsernameProfileScreen() {
  const params = useLocalSearchParams<{ tab?: string; username?: string }>();
  const username =
    typeof params.username === 'string' ? params.username.toLowerCase() : '';
  const requestedTab = Array.isArray(params.tab) ? params.tab[0] : params.tab;
  const [data, setData] = useState<PublicProfilePayload | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ProfileContentTab>(
    requestedTab === 'garage' ? 'garage' : 'posts',
  );
  const [videoViewerPostId, setVideoViewerPostId] = useState<string | null>(
    null,
  );
  const [videoViewerInitialPost, setVideoViewerInitialPost] =
    useState<PublicPost | null>(null);
  const [photoViewerPostId, setPhotoViewerPostId] = useState<string | null>(
    null,
  );
  const [photoViewerInitialPost, setPhotoViewerInitialPost] =
    useState<PublicPost | null>(null);
  const [photoViewerInitialIndex, setPhotoViewerInitialIndex] = useState(0);
  const [showReactionBreakdown, setShowReactionBreakdown] = useState(false);
  const [viewerImage, setViewerImage] = useState<ProfileViewerImage | null>(
    null,
  );

  useEffect(() => {
    let active = true;

    setLoading(true);
    setError(null);

    void fetchPublicProfile(username)
      .then((nextData) => {
        if (active) {
          setData(nextData);
        }
      })
      .catch((caughtError) => {
        if (active) {
          setError(
            caughtError instanceof Error
              ? caughtError
              : new Error('Unable to load this profile.'),
          );
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [username]);

  useEffect(() => {
    setActiveTab(requestedTab === 'garage' ? 'garage' : 'posts');
  }, [requestedTab]);

  function changeProfileTab(nextTab: ProfileContentTab) {
    setActiveTab(nextTab);
    router.replace(
      nextTab === 'garage'
        ? `/profile/${encodeURIComponent(username)}?tab=garage`
        : `/profile/${encodeURIComponent(username)}`,
    );
  }

  const profile = data?.profile;
  const vehicles = useMemo(
    () =>
      data?.vehicles.map((vehicle) =>
        withLiveGarageVehicleOwnerProfile(vehicle, profile),
      ) ?? [],
    [data?.vehicles, profile],
  );
  const avatarURL = getProfileAvatarURL(profile, 'display');
  const avatarOriginalURL = getProfileAvatarOriginalURL(profile);
  const coverURL = getProfileCoverPhotoURL(profile, 'hero');
  const coverOriginalURL = getProfileCoverOriginalURL(profile);
  const fullName = profile?.fullName ?? 'Frienzzi user';
  const initial = fullName.slice(0, 1).toUpperCase();
  const stats = data?.stats ?? {
    blockedByCount: 0,
    followerCount: profile?.followerCount ?? 0,
    followingCount: profile?.followingCount ?? 0,
    inviteCount: profile?.inviteCount ?? 0,
    reactionCounts: emptyProfileReactionCounts(),
    totalLikeCount: 0,
  };
  const reactionTotal = totalProfileReactions(stats);

  return (
    <>
      <PublicPageShell maxWidth={720}>
        {loading ? (
          <View style={styles.loading}>
            <ActivityIndicator color={colors.primary} />
            <Text style={styles.loadingText}>Loading profile...</Text>
          </View>
        ) : error ? (
          <ErrorView
            message={error.message}
            onRetry={() => {
              setLoading(true);
              setError(null);
              void fetchPublicProfile(username)
                .then(setData)
                .catch((caughtError) =>
                  setError(
                    caughtError instanceof Error
                      ? caughtError
                      : new Error('Unable to load this profile.'),
                  ),
                )
                .finally(() => setLoading(false));
            }}
          />
        ) : !profile ? (
          <View style={styles.card}>
            <Text style={styles.emptyTitle}>Profile unavailable</Text>
            <Text style={styles.emptyText}>
              This profile link may be incorrect or the account may no longer be
              available.
            </Text>
            <Link href="/sign-up" asChild>
              <Button title="Create your Frienzzi profile" />
            </Link>
          </View>
        ) : (
          <>
            <View style={styles.profileCard}>
              <View style={styles.cover}>
                {coverURL ? (
                  <Pressable
                    accessibilityLabel={`${fullName}'s cover photo`}
                    accessibilityRole="imagebutton"
                    onPress={() => {
                      setViewerImage({
                        accessibilityLabel: `${fullName}'s cover photo`,
                        analyticsContext: 'profile_cover',
                        originalURL: coverOriginalURL ?? coverURL,
                        thumbnailURL: coverURL,
                        title: `${fullName}'s cover photo`,
                      });
                    }}
                    style={({ pressed }) => [
                      styles.coverImage,
                      pressed ? styles.photoPressed : null,
                    ]}
                  >
                    <Image
                      contentFit="cover"
                      source={{ uri: coverURL }}
                      style={styles.coverImage}
                    />
                  </Pressable>
                ) : (
                  <View style={styles.coverPlaceholder}>
                    <Text style={styles.coverPlaceholderText}>Frienzzi</Text>
                  </View>
                )}
              </View>
              <View style={styles.identityRow}>
                <View style={styles.avatar}>
                  {avatarURL ? (
                    <Pressable
                      accessibilityLabel={`${fullName}'s profile photo`}
                      accessibilityRole="imagebutton"
                      onPress={() => {
                        setViewerImage({
                          accessibilityLabel: `${fullName}'s profile photo`,
                          analyticsContext: 'profile_avatar',
                          originalURL: avatarOriginalURL ?? avatarURL,
                          thumbnailURL: avatarURL,
                          title: `${fullName}'s profile photo`,
                        });
                      }}
                      style={({ pressed }) => [
                        styles.avatarImage,
                        pressed ? styles.photoPressed : null,
                      ]}
                    >
                      <Image
                        contentFit="cover"
                        source={{ uri: avatarURL }}
                        style={styles.avatarImage}
                      />
                    </Pressable>
                  ) : (
                    <Text style={styles.avatarText}>{initial}</Text>
                  )}
                </View>
                <View style={styles.identityText}>
                  <IdentityHeader
                    containerStyle={styles.profileIdentityHeader}
                    density="profile"
                    founderBadge={profile.founderBadge === true}
                    fullName={fullName}
                    interactive={false}
                    showAvatar={false}
                    username={profile.username}
                    verifiedBadge={profile.verifiedBadge === true}
                  />
                  {profile.location ? (
                    <Text style={styles.meta}>{profile.location}</Text>
                  ) : null}
                </View>
              </View>
              {profile.bio ? (
                <Text style={styles.bio}>{profile.bio}</Text>
              ) : null}
              <View style={styles.statsRow}>
                <View style={styles.stat}>
                  <Text style={styles.statValue}>{stats.followerCount}</Text>
                  <Text style={styles.statLabel}>Followers</Text>
                </View>
                <View style={styles.stat}>
                  <Text style={styles.statValue}>{stats.followingCount}</Text>
                  <Text style={styles.statLabel}>Following</Text>
                </View>
                <View style={styles.stat}>
                  <Text style={styles.statValue}>{stats.inviteCount}</Text>
                  <Text style={styles.statLabel}>Invites</Text>
                </View>
                <Pressable
                  accessibilityLabel="View reaction breakdown"
                  accessibilityRole="button"
                  onPress={() => setShowReactionBreakdown(true)}
                  style={({ pressed }) => [
                    styles.stat,
                    pressed ? styles.statPressed : null,
                  ]}
                >
                  <Text style={styles.statValue}>{reactionTotal}</Text>
                  <Text style={styles.statLabel}>Reactions</Text>
                </Pressable>
              </View>
              <View style={styles.joinCta}>
                <Link href="/sign-up" asChild>
                  <Button title="Join Frienzzi to follow and comment" />
                </Link>
              </View>
            </View>
            <ProfileContentTabs
              activeTab={activeTab}
              onChange={changeProfileTab}
            />
            {activeTab === 'garage' ? (
              <GarageSection
                emptyText="Public Garage vehicles from this profile will appear here."
                vehicles={vehicles}
              />
            ) : (
              <View style={styles.postsSection}>
                <Text style={styles.sectionTitle}>Public posts</Text>
                {data.posts.length > 0 ? (
                  data.posts.map((post) => (
                    <PostCard
                      key={post.id}
                      post={post}
                      openDetailOnPress
                      onPhotoPress={(photoPost, photoIndex) => {
                        setPhotoViewerInitialPost(photoPost);
                        setPhotoViewerPostId(photoPost.id);
                        setPhotoViewerInitialIndex(photoIndex);
                      }}
                      onVideoPress={(videoPost) => {
                        setVideoViewerInitialPost(videoPost);
                        setVideoViewerPostId(videoPost.id);
                      }}
                    />
                  ))
                ) : (
                  <Text style={styles.emptyText}>
                    No public posts are available for this profile yet.
                  </Text>
                )}
              </View>
            )}
          </>
        )}
        {viewerImage ? (
          <PhotoViewer
            accessibilityLabel={viewerImage.accessibilityLabel}
            analyticsContext={viewerImage.analyticsContext}
            onClose={() => setViewerImage(null)}
            originalURL={viewerImage.originalURL}
            thumbnailURL={viewerImage.thumbnailURL}
            title={viewerImage.title}
            visible
          />
        ) : null}
      </PublicPageShell>
      <ProfileReactionBreakdownModal
        stats={stats}
        visible={showReactionBreakdown}
        onClose={() => setShowReactionBreakdown(false)}
      />
      <PostVideoViewerModal
        key={videoViewerPostId ?? 'post-video-viewer'}
        initialPost={videoViewerInitialPost}
        initialPostId={videoViewerPostId}
        posts={data?.posts ?? []}
        visible={Boolean(videoViewerPostId)}
        onClose={() => {
          setVideoViewerPostId(null);
          setVideoViewerInitialPost(null);
        }}
        onCommentPress={() => {
          setVideoViewerPostId(null);
          setVideoViewerInitialPost(null);
        }}
      />
      <PostPhotoViewerModal
        key={
          photoViewerPostId
            ? `${photoViewerPostId}:${photoViewerInitialIndex}`
            : 'post-photo-viewer'
        }
        initialPhotoIndex={photoViewerInitialIndex}
        initialPost={photoViewerInitialPost}
        initialPostId={photoViewerPostId}
        posts={data?.posts ?? []}
        visible={Boolean(photoViewerPostId)}
        onClose={() => {
          setPhotoViewerPostId(null);
          setPhotoViewerInitialPost(null);
          setPhotoViewerInitialIndex(0);
        }}
        onCommentPress={() => {
          setPhotoViewerPostId(null);
          setPhotoViewerInitialPost(null);
        }}
      />
    </>
  );
}

const styles = StyleSheet.create({
  loading: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  loadingText: {
    color: colors.mutedText,
    fontSize: 14,
    fontWeight: '700',
  },
  card: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    padding: spacing.xl,
  },
  emptyTitle: {
    color: colors.text,
    fontSize: 22,
    fontWeight: '900',
  },
  emptyText: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
  profileCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
  },
  cover: {
    aspectRatio: 592 / 164,
    backgroundColor: colors.surfaceTint,
  },
  coverImage: {
    height: '100%',
    width: '100%',
  },
  photoPressed: {
    opacity: 0.9,
  },
  coverPlaceholder: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  coverPlaceholderText: {
    color: colors.secondary,
    fontSize: 28,
    fontWeight: '900',
  },
  identityRow: {
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.lg,
  },
  avatar: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.surface,
    borderRadius: 46,
    borderWidth: 4,
    height: 92,
    justifyContent: 'center',
    marginTop: -48,
    overflow: 'hidden',
    width: 92,
  },
  avatarImage: {
    height: '100%',
    width: '100%',
  },
  avatarText: {
    color: colors.secondary,
    fontSize: 34,
    fontWeight: '900',
  },
  identityText: {
    flex: 1,
    gap: spacing.xs,
  },
  profileIdentityHeader: {
    alignSelf: 'stretch',
  },
  meta: {
    color: colors.mutedText,
    fontSize: 14,
  },
  bio: {
    color: colors.text,
    fontSize: 15,
    lineHeight: 22,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
  },
  statsRow: {
    borderTopColor: colors.border,
    borderTopWidth: 1,
    flexDirection: 'row',
  },
  stat: {
    alignItems: 'center',
    flex: 1,
    gap: spacing.xs,
    padding: spacing.md,
  },
  statPressed: {
    backgroundColor: colors.surfaceTint,
  },
  statValue: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  statLabel: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '800',
  },
  joinCta: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.lg,
  },
  postsSection: {
    gap: spacing.md,
  },
  sectionTitle: {
    color: colors.text,
    fontSize: 22,
    fontWeight: '900',
  },
});
